import React, { useState, useEffect, FormEvent } from 'react';
import { KeyRound } from 'lucide-react';
import { API_BASE } from '../../context/AuthContext';

interface Props {
  token: string;
  onSuccess: () => void;
}

type Stage = 'loading' | 'ready' | 'invalid' | 'done';

export default function AcceptInvitePage({ token, onSuccess }: Props) {
  const [stage,    setStage]    = useState<Stage>('loading');
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/api/auth/invite?token=${encodeURIComponent(token)}`)
      .then(r => r.json())
      .then((data: any) => {
        if (data.email) { setEmail(data.email); setStage('ready'); }
        else setStage('invalid');
      })
      .catch(() => setStage('invalid'));
  }, [token]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('As senhas não coincidem'); return; }
    if (password.length < 8)  { setError('Mínimo de 8 caracteres'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/accept`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ token, password }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).error ?? 'Erro ao criar conta');
      }
      setStage('done');
      setTimeout(onSuccess, 1800);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        {stage === 'loading' && (
          <p className="auth-loading">Verificando convite…</p>
        )}

        {stage === 'invalid' && (
          <>
            <div className="auth-logo">
              <div className="logo-mark">PI</div>
              <div><div className="logo-title">Pedbot</div><div className="logo-sub">Integrações</div></div>
            </div>
            <div className="auth-error" style={{ marginTop: 8 }}>
              Link inválido ou expirado. Solicite um novo convite ao administrador.
            </div>
          </>
        )}

        {stage === 'done' && (
          <>
            <div className="auth-logo">
              <div className="logo-mark">PI</div>
              <div><div className="logo-title">Pedbot</div><div className="logo-sub">Integrações</div></div>
            </div>
            <div className="invite-success">
              <div className="invite-success-icon">✓</div>
              <p>Conta criada. Redirecionando para o login…</p>
            </div>
          </>
        )}

        {stage === 'ready' && (
          <>
            <div className="auth-logo">
              <div className="logo-mark">PI</div>
              <div><div className="logo-title">Pedbot</div><div className="logo-sub">Integrações</div></div>
            </div>
            <h1 className="auth-title">Criar senha</h1>
            <p className="auth-sub">{email}</p>
            <form className="auth-form" onSubmit={handleSubmit}>
              <div className="auth-field">
                <label className="auth-label">Senha</label>
                <input
                  className="auth-input"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Mínimo 8 caracteres"
                  required
                  autoFocus
                />
              </div>
              <div className="auth-field">
                <label className="auth-label">Confirmar senha</label>
                <input
                  className="auth-input"
                  type="password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>
              {error && <div className="auth-error">{error}</div>}
              <button className="auth-submit" type="submit" disabled={loading}>
                {loading
                  ? 'Criando conta…'
                  : <><KeyRound size={15} /> Criar conta</>
                }
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
